import { 
  CustomerStats, 
  CustomerPreferences, 
  CustomerReport, 
  DateRange, 
  CustomerFilters 
} from '../models/CustomerStats';
import { Order } from '../models/Order';
import orderService from './orderService';
import { storeService } from './storeService';
import customerAnalyticsScopeDailyService, {
  CustomerAnalyticsAggregateScope,
} from './customerAnalyticsScopeDailyService';

type CustomerAnalyticsScope = {
  storeId?: string;
  allowedStoreIds?: string[];
  aggregateScope?: CustomerAnalyticsAggregateScope;
};

class CustomerAnalyticsService {
  private filterOrdersByScope(orders: Order[], scope?: CustomerAnalyticsScope): Order[] {
    if (!scope) {
      return orders;
    }

    if (scope.storeId) {
      return orders.filter((order) => order.storeId === scope.storeId);
    }

    if (scope.allowedStoreIds && scope.allowedStoreIds.length > 0) {
      const allowed = new Set(scope.allowedStoreIds);
      return orders.filter((order) => allowed.has(order.storeId));
    }

    return orders;
  }
  
  // Calculer les statistiques des clients
  async calculateCustomerStats(dateRange?: DateRange, scope?: CustomerAnalyticsScope): Promise<CustomerStats[]> {
    try {
      if (dateRange && scope?.aggregateScope) {
        try {
          const aggregateCustomers = await customerAnalyticsScopeDailyService.getCustomerStats(
            scope.aggregateScope,
            dateRange
          );
          if (aggregateCustomers !== null) {
            return aggregateCustomers;
          }
        } catch (aggregateError) {
          console.warn('customer_analytics_scope_daily indisponible, fallback orders:', aggregateError);
        }
      }

      // Récupérer tous les ordres de la période
      const orders = await orderService.getOrders(
        dateRange || scope?.storeId
          ? {
              ...(dateRange ? { dateRange } : {}),
              ...(scope?.storeId ? { storeId: scope.storeId } : {}),
            }
          : undefined,
        { enrichUsers: false, enrichProducts: false }
      );
      const scopedOrders = this.filterOrdersByScope(orders, scope);
      
      // Grouper les ordres par userId
      const customerOrdersMap = new Map<string, Order[]>();
      
      scopedOrders.forEach(order => {
        if (!customerOrdersMap.has(order.userId)) {
          customerOrdersMap.set(order.userId, []);
        }
        customerOrdersMap.get(order.userId)!.push(order);
      });

      // Calculer les statistiques pour chaque client
      const customerStats: CustomerStats[] = [];
      
      customerOrdersMap.forEach((userOrders, userId) => {
        const stats = this.calculateStatsForCustomer(userId, userOrders);
        customerStats.push(stats);
      });

      return customerStats.sort((a, b) => b.totalOrders - a.totalOrders);
    } catch (error) {
      console.error('Erreur lors du calcul des statistiques clients:', error);
      throw new Error('Impossible de calculer les statistiques clients');
    }
  }

  // Calculer les statistiques pour un client spécifique
  private calculateStatsForCustomer(userId: string, orders: Order[]): CustomerStats {
    if (orders.length === 0) {
      throw new Error(`Aucun ordre trouvé pour le client ${userId}`);
    }

    // Trier les ordres par date
    const sortedOrders = orders.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    const firstOrder = sortedOrders[0];
    const lastOrder = sortedOrders[sortedOrders.length - 1];

    // Calculer les totaux
    const totalSpent = orders.reduce((sum, order) => sum + order.totalAmount, 0);
    const averageOrderValue = totalSpent / orders.length;

    // Analyser les préférences
    const paymentMethods = orders.map(order => order.paymentMethod);
    const deliveryMethods = orders.map(order => order.deliveryMethod);
    const stores = orders.map(order => order.storeId);
    const drivers = orders
      .filter(order => order.deliveryPerson)
      .map(order => order.deliveryPerson!.id);

    // Calculer la fréquence de commande
    const daysSinceFirst = Math.max(1, (lastOrder.createdAt.getTime() - firstOrder.createdAt.getTime()) / (1000 * 60 * 60 * 24));
    const orderFrequency = (orders.length / daysSinceFirst) * 30; // Commandes par mois

    // Calculer l'ancienneté du client
    const customerSince = Math.max(1, (Date.now() - firstOrder.createdAt.getTime()) / (1000 * 60 * 60 * 24 * 30)); // Mois

    // Extraire les produits favoris
    const productCounts = new Map<string, number>();
    orders.forEach(order => {
      order.items.forEach(item => {
        const key = `${item.productId}-${item.size}`;
        productCounts.set(key, (productCounts.get(key) || 0) + item.quantity);
      });
    });

    const favoriteProducts = Array.from(productCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([productKey]) => productKey);

    return {
      userId,
      clientName: firstOrder.clientName || 'Client inconnu',
      clientPhone: firstOrder.clientPhone || 'Non disponible',
      totalOrders: orders.length,
      totalSpent,
      averageOrderValue,
      lastOrderDate: lastOrder.createdAt,
      firstOrderDate: firstOrder.createdAt,
      favoriteProducts,
      preferredPaymentMethod: this.getMostFrequent(paymentMethods),
      preferredDeliveryMethod: this.getMostFrequent(deliveryMethods),
      orderFrequency,
      customerSince,
      mostOrderedStore: this.getMostFrequent(stores),
      preferredDriver: drivers.length > 0 ? this.getMostFrequent(drivers) : undefined
    };
  }

  // Obtenir l'élément le plus fréquent dans un tableau
  private getMostFrequent<T>(items: T[]): T {
    const counts = new Map<T, number>();
    items.forEach(item => {
      counts.set(item, (counts.get(item) || 0) + 1);
    });

    let mostFrequent = items[0];
    let maxCount = 0;

    counts.forEach((count, item) => {
      if (count > maxCount) {
        maxCount = count;
        mostFrequent = item;
      }
    });

    return mostFrequent;
  }

  // Obtenir les top clients par nombre de commandes
  getTopCustomersByOrders(customers: CustomerStats[], limit: number = 10): CustomerStats[] {
    return [...customers]
      .sort((a, b) => b.totalOrders - a.totalOrders)
      .slice(0, limit);
  }

  // Obtenir les top clients par montant dépensé
  getTopCustomersBySpending(customers: CustomerStats[], limit: number = 10): CustomerStats[] {
    return [...customers]
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, limit);
  }

  // Obtenir les top clients par fréquence de commande
  getTopCustomersByFrequency(customers: CustomerStats[], limit: number = 10): CustomerStats[] {
    return [...customers]
      .sort((a, b) => b.orderFrequency - a.orderFrequency)
      .slice(0, limit);
  }

  // Obtenir les clients inactifs
  getInactiveCustomers(customers: CustomerStats[], daysSinceLastOrder: number = 30): CustomerStats[] {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysSinceLastOrder);

    return customers.filter(customer => customer.lastOrderDate < cutoffDate);
  }

  // Obtenir les nouveaux clients
  getNewCustomers(customers: CustomerStats[], dateRange: DateRange): CustomerStats[] {
    return customers.filter(customer => 
      customer.firstOrderDate >= dateRange.start && 
      customer.firstOrderDate <= dateRange.end
    );
  }

  // Obtenir les clients réguliers
  getRegularCustomers(customers: CustomerStats[], minOrdersPerMonth: number = 2): CustomerStats[] {
    return customers.filter(customer => customer.orderFrequency >= minOrdersPerMonth);
  }

  // Analyser les préférences d'un client
  async analyzeCustomerPreferences(customerId: string): Promise<CustomerPreferences> {
    try {
      const orders = await orderService.getOrders({ searchTerm: customerId });
      const customerOrders = orders.filter(order => order.userId === customerId);

      if (customerOrders.length === 0) {
        throw new Error(`Aucun ordre trouvé pour le client ${customerId}`);
      }

      // Analyser les produits favoris
      const productCounts = new Map<string, { count: number; productId: string }>();
      customerOrders.forEach(order => {
        order.items.forEach(item => {
          const key = `${item.productId}-${item.size}`;
          if (!productCounts.has(key)) {
            productCounts.set(key, { count: 0, productId: item.productId });
          }
          productCounts.get(key)!.count += item.quantity;
        });
      });

      const favoriteProducts = Array.from(productCounts.entries())
        .sort((a, b) => b[1].count - a[1].count)
        .slice(0, 5)
        .map(([productKey, data]) => ({
          productId: data.productId,
          productName: productKey, // TODO: Récupérer le vrai nom du produit
          orderCount: data.count
        }));

      // Analyser les méthodes de paiement
      const paymentMethodStats = {
        cash: customerOrders.filter(order => order.paymentMethod === 'cash').length,
        card: customerOrders.filter(order => order.paymentMethod === 'card').length
      };

      // Analyser les méthodes de livraison
      const deliveryMethodStats = {
        delivery: customerOrders.filter(order => order.deliveryMethod === 'delivery').length,
        pickup: customerOrders.filter(order => order.deliveryMethod === 'pickup').length
      };

      // Analyser les patterns temporels
      const hourDistribution = new Array(24).fill(0);
      const dayDistribution = new Array(7).fill(0);

      customerOrders.forEach(order => {
        const hour = order.createdAt.getHours();
        const day = order.createdAt.getDay();
        hourDistribution[hour]++;
        dayDistribution[day]++;
      });

      return {
        favoriteProducts,
        paymentMethodStats,
        deliveryMethodStats,
        orderTimePatterns: {
          hourDistribution,
          dayDistribution
        }
      };
    } catch (error) {
      console.error(`Erreur lors de l'analyse des préférences du client ${customerId}:`, error);
      throw error;
    }
  }

  // Obtenir l'historique des commandes d'un client
  async getCustomerOrderHistory(customerId: string): Promise<Order[]> {
    try {
      const orders = await orderService.getOrders();
      return orders
        .filter(order => order.userId === customerId)
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'historique du client ${customerId}:`, error);
      throw error;
    }
  }

  // Filtrer les clients selon des critères
  filterCustomers(customers: CustomerStats[], filters: CustomerFilters): CustomerStats[] {
    return customers.filter(customer => {
      // Filtre par nombre de commandes
      if (filters.minOrders && customer.totalOrders < filters.minOrders) return false;
      if (filters.maxOrders && customer.totalOrders > filters.maxOrders) return false;

      // Filtre par montant dépensé
      if (filters.minSpent && customer.totalSpent < filters.minSpent) return false;
      if (filters.maxSpent && customer.totalSpent > filters.maxSpent) return false;

      // Filtre par méthode de paiement préférée
      if (filters.paymentMethod && customer.preferredPaymentMethod !== filters.paymentMethod) return false;

      // Filtre par méthode de livraison préférée
      if (filters.deliveryMethod && customer.preferredDeliveryMethod !== filters.deliveryMethod) return false;

      // Filtre par magasin
      if (filters.storeId && customer.mostOrderedStore !== filters.storeId) return false;

      // Filtre par activité
      if (filters.isActive !== undefined) {
        const daysSinceLastOrder = (Date.now() - customer.lastOrderDate.getTime()) / (1000 * 60 * 60 * 24);
        const isActive = daysSinceLastOrder <= 30;
        if (filters.isActive !== isActive) return false;
      }

      // Filtre par plage de dates
      if (filters.dateRange) {
        if (customer.firstOrderDate < filters.dateRange.start || 
            customer.firstOrderDate > filters.dateRange.end) return false;
      }

      return true;
    });
  }

  // Générer un rapport complet des clients
  async generateCustomerReport(dateRange?: DateRange, scope?: CustomerAnalyticsScope): Promise<CustomerReport> {
    try {
      const customers = await this.calculateCustomerStats(dateRange, scope);
      
      // Calculer les métriques générales
      const totalCustomers = customers.length;
      const activeCustomers = customers.filter(customer => {
        const daysSinceLastOrder = (Date.now() - customer.lastOrderDate.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceLastOrder <= 30;
      }).length;

      const newCustomers = dateRange ? 
        this.getNewCustomers(customers, dateRange).length : 
        customers.filter(customer => {
          const daysSinceFirst = (Date.now() - customer.firstOrderDate.getTime()) / (1000 * 60 * 60 * 24);
          return daysSinceFirst <= 30;
        }).length;

      const totalRevenue = customers.reduce((sum, customer) => sum + customer.totalSpent, 0);
      const averageOrderValue = totalRevenue / customers.reduce((sum, customer) => sum + customer.totalOrders, 0);

      // Segmenter les clients
      const customerSegments = {
        vip: customers.filter(customer => customer.totalOrders > 20),
        regular: customers.filter(customer => customer.totalOrders >= 5 && customer.totalOrders <= 20),
        occasional: customers.filter(customer => customer.totalOrders >= 2 && customer.totalOrders <= 4),
        oneTime: customers.filter(customer => customer.totalOrders === 1)
      };

      return {
        totalCustomers,
        activeCustomers,
        newCustomers,
        averageOrderValue,
        totalRevenue,
        topCustomers: this.getTopCustomersBySpending(customers, 10),
        customerSegments
      };
    } catch (error) {
      console.error('Erreur lors de la génération du rapport client:', error);
      throw new Error('Impossible de générer le rapport client');
    }
  }

  // Exporter les statistiques clients en CSV
  exportCustomerStatsToCSV(customers: CustomerStats[]): string {
    const headers = [
      'ID Client',
      'Nom',
      'Téléphone',
      'Nombre de Commandes',
      'Total Dépensé',
      'Panier Moyen',
      'Fréquence (commandes/mois)',
      'Client Depuis (mois)',
      'Dernière Commande',
      'Paiement Préféré',
      'Livraison Préférée',
      'Magasin Principal'
    ];

    const rows = customers.map(customer => [
      customer.userId,
      customer.clientName,
      customer.clientPhone,
      customer.totalOrders.toString(),
      customer.totalSpent.toFixed(2) + ' MAD',
      customer.averageOrderValue.toFixed(2) + ' MAD',
      customer.orderFrequency.toFixed(1),
      customer.customerSince.toFixed(1),
      customer.lastOrderDate.toLocaleDateString('fr-FR'),
      customer.preferredPaymentMethod === 'cash' ? 'Espèces' : 'Carte',
      customer.preferredDeliveryMethod === 'delivery' ? 'Livraison' : 'À emporter',
      customer.mostOrderedStore
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    return csvContent;
  }

  // Obtenir les statistiques rapides pour le dashboard
  async getQuickStats(dateRange?: DateRange, scope?: CustomerAnalyticsScope): Promise<{
    totalCustomers: number;
    activeCustomers: number;
    newCustomers: number;
    vipCustomers: number;
    averageOrdersPerCustomer: number;
    customerRetentionRate: number;
  }> {
    try {
      const customers = await this.calculateCustomerStats(dateRange, scope);
      
      const totalCustomers = customers.length;
      const activeCustomers = customers.filter(customer => {
        const daysSinceLastOrder = (Date.now() - customer.lastOrderDate.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceLastOrder <= 30;
      }).length;

      const newCustomers = customers.filter(customer => {
        const daysSinceFirst = (Date.now() - customer.firstOrderDate.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceFirst <= 30;
      }).length;

      const vipCustomers = customers.filter(customer => customer.totalOrders > 20).length;
      
      const averageOrdersPerCustomer = customers.reduce((sum, customer) => sum + customer.totalOrders, 0) / totalCustomers;
      
      const returningCustomers = customers.filter(customer => customer.totalOrders > 1).length;
      const customerRetentionRate = (returningCustomers / totalCustomers) * 100;

      return {
        totalCustomers,
        activeCustomers,
        newCustomers,
        vipCustomers,
        averageOrdersPerCustomer,
        customerRetentionRate
      };
    } catch (error) {
      console.error('Erreur lors du calcul des statistiques rapides:', error);
      throw new Error('Impossible de calculer les statistiques rapides');
    }
  }

  // Exporter en XLS (compatibilité Excel via table HTML)
  exportCustomerStatsToXLS(customers: CustomerStats[]): string {
    const headers = [
      'ID Client',
      'Nom',
      'Téléphone',
      'Nombre de Commandes',
      'Total Dépensé (MAD)',
      'Panier Moyen (MAD)',
      'Fréquence (commandes/mois)',
      'Client Depuis (mois)',
      'Dernière Commande',
      'Paiement Préféré',
      'Livraison Préférée',
      'Magasin Principal'
    ];

    const rows = customers.map(c => ([
      c.userId,
      c.clientName,
      c.clientPhone,
      String(c.totalOrders),
      c.totalSpent.toFixed(2),
      c.averageOrderValue.toFixed(2),
      c.orderFrequency.toFixed(1),
      c.customerSince.toFixed(1),
      c.lastOrderDate.toLocaleDateString('fr-FR'),
      c.preferredPaymentMethod === 'cash' ? 'Espèces' : 'Carte',
      c.preferredDeliveryMethod === 'delivery' ? 'Livraison' : 'À emporter',
      c.mostOrderedStore
    ]));

    const tableRows = rows.map(r => `<tr>${r.map(cell => `<td style="mso-number-format:'\\@'; padding:4px; border:1px solid #ccc;">${String(cell).replace(/"/g, '"')}</td>`).join('')}</tr>`).join('');

    const html =
      '<html><head><meta charset="UTF-8"></head><body>' +
      '<table border="1" cellspacing="0" cellpadding="3" style="border-collapse:collapse;">' +
      `<thead><tr>${headers.map(h => `<th style="background:#f5f5f5; padding:4px; border:1px solid #ccc;">${h}</th>`).join('')}</tr></thead>` +
      `<tbody>${tableRows}</tbody>` +
      '</table>' +
      '</body></html>';

    return html;
  }

  // Exporter une page imprimable (pour PDF via impression du navigateur)
  exportCustomerStatsToPrintableHTML(customers: CustomerStats[]): string {
    const style = `
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; }
        h1 { font-size: 18px; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; font-size: 12px; }
        th { background: #f5f5f5; text-align: left; }
        .right { text-align: right; }
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
        }
      </style>
    `;

    const headers = [
      'ID Client',
      'Nom',
      'Téléphone',
      'Nb Cmd',
      'Total (MAD)',
      'Panier (MAD)',
      'Fréq/mois',
      'Depuis (mois)',
      'Dernière Cmd',
      'Paiement',
      'Livraison',
      'Magasin'
    ];

    const rows = customers.map(c => ([
      c.userId,
      c.clientName,
      c.clientPhone,
      String(c.totalOrders),
      c.totalSpent.toFixed(2),
      c.averageOrderValue.toFixed(2),
      c.orderFrequency.toFixed(1),
      c.customerSince.toFixed(1),
      c.lastOrderDate.toLocaleDateString('fr-FR'),
      c.preferredPaymentMethod === 'cash' ? 'Espèces' : 'Carte',
      c.preferredDeliveryMethod === 'delivery' ? 'Livraison' : 'À emporter',
      c.mostOrderedStore
    ]));

    const tableRows = rows.map(r => `<tr>${r.map((cell, i) => {
      const align = [3,4,5,6,7].includes(i) ? ' class="right"' : '';
      return `<td${align}>${String(cell).replace(/&/g, '&').replace(/</g, '<').replace(/>/g, '>')}</td>`;
    }).join('')}</tr>`).join('');

    const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
          ${style}
          <title>Analyse Clients - Export PDF</title>
        </head>
        <body>
          <h1>Analyse des Clients - Export</h1>
          <table>
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              setTimeout(function(){ window.print(); }, 300);
            }
          </script>
        </body>
      </html>
    `;
    return html;
  }
  // ===== Helpers: resolve store name from ID with cache =====
  private storeNameCache = new Map<string, string>();

  private async getStoreNameById(id?: string): Promise<string> {
    try {
      if (!id) return '';
      const cached = this.storeNameCache.get(id);
      if (cached) return cached;
      const store = await storeService.getStoreById(id);
      const name = store?.name || id;
      this.storeNameCache.set(id, name);
      return name;
    } catch {
      return id || '';
    }
  }

  // ===== Async exports with store names and no userId column =====
  async exportCustomerStatsToCSVWithNames(customers: CustomerStats[]): Promise<string> {
    const headers = [
      'Nom',
      'Téléphone',
      'Nombre de Commandes',
      'Total Dépensé (MAD)',
      'Panier Moyen (MAD)',
      'Fréquence (commandes/mois)',
      'Client Depuis (mois)',
      'Dernière Commande',
      'Paiement Préféré',
      'Livraison Préférée',
      'Magasin Principal'
    ];

    const storeNames = await Promise.all(customers.map(c => this.getStoreNameById(c.mostOrderedStore)));

    const rows = customers.map((c, i) => [
      c.clientName,
      c.clientPhone,
      String(c.totalOrders),
      c.totalSpent.toFixed(2),
      c.averageOrderValue.toFixed(2),
      c.orderFrequency.toFixed(1),
      c.customerSince.toFixed(1),
      c.lastOrderDate.toLocaleDateString('fr-FR'),
      c.preferredPaymentMethod === 'cash' ? 'Espèces' : 'Carte',
      c.preferredDeliveryMethod === 'delivery' ? 'Livraison' : 'À emporter',
      storeNames[i]
    ]);

    return [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
  }

  async exportCustomerStatsToXLSWithNames(customers: CustomerStats[]): Promise<string> {
    const headers = [
      'Nom',
      'Téléphone',
      'Nombre de Commandes',
      'Total Dépensé (MAD)',
      'Panier Moyen (MAD)',
      'Fréquence (commandes/mois)',
      'Client Depuis (mois)',
      'Dernière Commande',
      'Paiement Préféré',
      'Livraison Préférée',
      'Magasin Principal'
    ];

    const storeNames = await Promise.all(customers.map(c => this.getStoreNameById(c.mostOrderedStore)));

    const rows = customers.map((c, i) => ([
      c.clientName,
      c.clientPhone,
      String(c.totalOrders),
      c.totalSpent.toFixed(2),
      c.averageOrderValue.toFixed(2),
      c.orderFrequency.toFixed(1),
      c.customerSince.toFixed(1),
      c.lastOrderDate.toLocaleDateString('fr-FR'),
      c.preferredPaymentMethod === 'cash' ? 'Espèces' : 'Carte',
      c.preferredDeliveryMethod === 'delivery' ? 'Livraison' : 'À emporter',
      storeNames[i]
    ]));

    const tableRows = rows.map(r => `<tr>${r.map(cell => `<td style="mso-number-format:'\\@'; padding:4px; border:1px solid #ccc;">${String(cell).replace(/&/g, '&').replace(/</g, '<').replace(/"/g, '"').replace(/>/g, '>')}</td>`).join('')}</tr>`).join('');

    const html =
      '<html><head><meta charset="UTF-8"></head><body>' +
      '<table border="1" cellspacing="0" cellpadding="3" style="border-collapse:collapse;">' +
      `<thead><tr>${headers.map(h => `<th style="background:#f5f5f5; padding:4px; border:1px solid #ccc;">${h}</th>`).join('')}</tr></thead>` +
      `<tbody>${tableRows}</tbody>` +
      '</table>' +
      '</body></html>';

    return html;
  }

  async exportCustomerStatsToPrintableHTMLWithNames(customers: CustomerStats[]): Promise<string> {
    const style = `
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; }
        h1 { font-size: 18px; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; font-size: 12px; }
        th { background: #f5f5f5; text-align: left; }
        .right { text-align: right; }
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
        }
      </style>
    `;

    const headers = [
      'Nom',
      'Téléphone',
      'Nb Cmd',
      'Total (MAD)',
      'Panier (MAD)',
      'Fréq/mois',
      'Depuis (mois)',
      'Dernière Cmd',
      'Paiement',
      'Livraison',
      'Magasin'
    ];

    const storeNames = await Promise.all(customers.map(c => this.getStoreNameById(c.mostOrderedStore)));

    const rows = customers.map((c, i) => ([
      c.clientName,
      c.clientPhone,
      String(c.totalOrders),
      c.totalSpent.toFixed(2),
      c.averageOrderValue.toFixed(2),
      c.orderFrequency.toFixed(1),
      c.customerSince.toFixed(1),
      c.lastOrderDate.toLocaleDateString('fr-FR'),
      c.preferredPaymentMethod === 'cash' ? 'Espèces' : 'Carte',
      c.preferredDeliveryMethod === 'delivery' ? 'Livraison' : 'À emporter',
      storeNames[i]
    ]));

    const tableRows = rows.map(r => `<tr>${r.map((cell, i) => {
      const align = [2,3,4,5,6].includes(i) ? ' class="right"' : '';
      return `<td${align}>${String(cell).replace(/&/g, '&').replace(/</g, '<').replace(/>/g, '>')}</td>`;
    }).join('')}</tr>`).join('');

    const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
          ${style}
          <title>Analyse Clients - Export PDF</title>
        </head>
        <body>
          <h1>Analyse des Clients - Export</h1>
          <table>
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              setTimeout(function(){ window.print(); }, 300);
            }
          </script>
        </body>
      </html>
    `;
    return html;
  }
}

const customerAnalyticsService = new CustomerAnalyticsService();
export default customerAnalyticsService;
